import React, {useState, useEffect, useRef} from 'react'
import PropTypes from 'prop-types';
import { LocalizeContextProps, Translate, withLocalize } from 'react-localize-redux';
import { Search as SearchPatientIcon } from "@mui/icons-material";
import { Box, Grid, Paper, Typography, Button, FormControlLabel, Checkbox, CircularProgress } from '@mui/material';
import Form  from '../../components/general/form';
import {useHistory, useParams} from 'react-router-dom';
import Loader from '../../components/Loader';
import PatientsTable from '../../components/general/PatientsTable';
import { ButtonBack, ButtonCancel, ButtonContinue, GridContainer } from '../../components/general/mini_components';
import { connect } from 'react-redux';
import { useDispatch } from "react-redux";
import { incrementalSyncPatientsAction } from '../../redux/actions/patientsActions';
import _ from 'lodash';
import Modal from '../../components/general/modal';
import { DepartmentsAccordionRedux } from './departments/DepartmentsAccordion';
import { decryptSinglePatientData, getPatientID, yearsFromDate } from '../../utils/index.jsx';
import ICT from '../../components/general/SmartFields/ICT';
import { searchPatientByDiagnosis } from '../../services';
import { getPatientsWithAppointmentTodayService } from '../../services/agenda';
import PatientAppointmentInfo from './Outpatients/PatientAppointmentInfo';
import { HOSPITAL_BILLING_PATIENT, HOSPITAL_PATIENT } from '../../routes/urls';
import { TabsSherwood } from '../components/Tabs';
import RequestForm from './Request/RequestForm';
import { ServiceType } from './Request/types';
import { IPatient, PatientStatus, FUNCTIONALITY } from '../../constants/types';
import PatientInfo from '../../components/PatientInfo';
import { fetchPatient } from '../../db';

let personalFieldsForm = {};
const ID_FIELD = {
    name : "id",
    required: false,
    type : "text",
    label : "id",
    order: 0
}

function SearchPatients(props: any){
    const [valuesSearch, setValuesSearch] = useState(null);
    const [showResults, setShowResults] = useState(false);
    const [filteredPatients, setFilteredPatients] = useState<IPatient[]>([]);
    const [patientsDiagnoseLoading, setPatientsDiagnoseLoading] = useState(false);
    const [patientsTodayLoading, setPatientsTodayLoading] = useState(false);
    const insurances = props.investigations.currentInvestigation ? props.investigations.currentInvestigation.insurances : [];
    const coverages = props.investigations.currentInvestigation ? props.investigations.currentInvestigation.coverages : [];
    const outpatientsInfo = props.investigations.currentInvestigation ? props.investigations.currentInvestigation.outpatientsInfo : {};
    const patients = props.patients.data && props.investigations.currentInvestigation ? props.patients.data[props.investigations.currentInvestigation.uuid] : [];
    const [actionPatient, setActionPatient] = useState<PatientAction | null>(null);
    const [includeDeceased, setIncludeDeceased] = useState(false);
    const dispatch = useDispatch();
    const history = useHistory();
    const { action, uuidPatient } = useParams();
    const [selectedPatient, setSelectedPatient] = useState<IPatient | null>(null);
    const [filtering, setFiltering] = useState(false);
    const workerRef = useRef<Worker | null>(null);

    // Initialize Web Worker for patient filtering (off main thread)
    useEffect(() => {
        workerRef.current = new Worker(
            new URL('../../workers/patientFilter.worker.ts', import.meta.url),
            { type: 'module' }
        );
        workerRef.current.onmessage = (e) => {
            if (e.data.type === 'results') {
                setFilteredPatients(e.data.patients);
                setShowResults(true);
                setFiltering(false);
            }
        };
        return () => {
            workerRef.current?.terminate();
        };
    }, []);

    async function searchPatientCallBack(values: any) {
        console.log(values);
        setValuesSearch(values);
        
        // Fetch any updated patients using event-based incremental sync (faster)
        // Note: silent = false triggers loading state, which then triggers the useEffect for filtering
        await dispatch(incrementalSyncPatientsAction(props.investigations.currentInvestigation.uuid, false)); 
        
    }
    function diagnoseSelectedCallBack(ict: any) {
        setPatientsDiagnoseLoading(true);
        searchPatientByDiagnosis(props.investigations.currentInvestigation.uuid, ict["ict-code"])
        .then(response => {
            setShowResults(true); 
            const decryptedPatients = response.patients.map((patient: IPatient) => {
                patient.personalData = decryptSinglePatientData(patient.personalData, props.investigations.currentInvestigation);
                return patient;
            })
            setFilteredPatients(decryptedPatients);
            setPatientsDiagnoseLoading(false);
        })
        .catch(error =>{
            console.error(error);
            setPatientsDiagnoseLoading(false);
        })
    }
    // SHER-353: fetch the cohort of patients with an appointment today (server resolves
    // "today" in the agenda timezone), then hydrate the encrypted patient records from the
    // local store — the endpoint only returns uuids, never PII.
    async function searchAppointmentTodayCallBack() {
        setPatientsTodayLoading(true);
        try {
            const uuidInvestigation = props.investigations.currentInvestigation.uuid;
            // Close any sync gap silently (don't trigger the personal-search worker effect).
            await dispatch(incrementalSyncPatientsAction(uuidInvestigation, true));
            const response = await getPatientsWithAppointmentTodayService(uuidInvestigation);
            // Preserve the server order (earliest appointment first); drop uuids not in the
            // local store after sync (rare).
            const hydrated = await Promise.all(response.patients.map(p => fetchPatient(p.uuid)));
            const matched = hydrated.filter(Boolean) as IPatient[];
            if (matched.length !== response.patients.length) {
                // Sync-gap: server returned a patient not yet in the local store. Rare; log for
                // field debugging rather than failing — the visible list is just short by that many.
                console.warn(`patients-today: ${response.patients.length - matched.length} patient(s) returned by the server are not in the local store (sync gap)`);
            }
            setFilteredPatients(matched);
            setShowResults(true);
        } catch (error) {
            console.error(error);
        } finally {
            setPatientsTodayLoading(false);
        }
    }
    function backToSearchCallBack() {
        setFilteredPatients([]);
        setShowResults(false);
    }
    
    async function patientSelectedCallBack(uuid: string) {
        const nextUrl = HOSPITAL_PATIENT.replace(":uuidPatient", uuid)
        history.push(nextUrl);
    }

    async function billingPatientCallBack(uuid: string) {
        const nextUrl = HOSPITAL_BILLING_PATIENT.replace(":uuidPatient", uuid)
        history.push(nextUrl);
    }

    
    // Dispatch filtering to Web Worker when sync completes
    useEffect(() => {
        if (!props.patients.loading && valuesSearch !== null && workerRef.current) {
            const hasDeceasedWorkflow = props.investigations.currentInvestigation?.functionalities?.includes("DECEASED_WORKFLOW");
            setFiltering(true);
            workerRef.current.postMessage({
                type: 'filter',
                patients,
                searchValues: valuesSearch,
                personalFields: props.investigations.currentInvestigation.personalFields,
                includeDeceased,
                hasDeceasedWorkflow,
            });
        }
    }, [props.patients.loading, includeDeceased])

    // Handle URL parameters
    useEffect(() => {
        if (uuidPatient && patients.length > 0) {
            const patient = patients.find(p => p.uuid === uuidPatient);
            if (patient) {
                setFilteredPatients([patient]);
                setShowResults(true);
                
                if (action) {
                    setActionPatient({action: action, patient: patient});
                }
            }
        }
    }, [uuidPatient, patients, action]);

    if(!props.investigations.data || !props.investigations.currentInvestigation || props.patients.loading || patientsDiagnoseLoading || filtering){
        // The bulk-download progress is rendered by the global blocking overlay in
        // layouts/Dashboard.jsx (driven by patients.downloading). This guard keeps
        // the page from rendering with partial data while patients.loading is set
        // by other flows (saves, incremental syncs) and covers page-local states
        // (filtering, diagnoses).
        return <Loader />;
    }
    return(
        <React.Fragment>
            <SearchPatientsComponent showResults={showResults}
                patients={filteredPatients} investigation={props.investigations.currentInvestigation}
                personalFields={props.investigations.currentInvestigation.personalFields}
                permissions={props.investigations.currentInvestigation.permissions}
                functionalities={props.investigations.currentInvestigation.functionalities}
                insurances={insurances} coverages={coverages} outpatientsInfo={outpatientsInfo}
                initActionPatient={actionPatient}
                includeDeceased={includeDeceased}
                setIncludeDeceased={setIncludeDeceased}
                searchPatientCallBack={searchPatientCallBack}
                searchAppointmentTodayCallBack={searchAppointmentTodayCallBack}
                patientsTodayLoading={patientsTodayLoading}
                backToSearchCallBack={backToSearchCallBack}
                patientSelectedCallBack={patientSelectedCallBack}
                billingPatientCallBack={billingPatientCallBack}
                diagnoseSelectedCallBack={diagnoseSelectedCallBack}
                    />
        </React.Fragment>                          
    )
}

const mapStateToProps = (state) =>{
    return {
        patients : state.patients,
        investigations:state.investigations
    }
}

SearchPatients.propTypes = {
    personalFields:PropTypes.array,
}

export default connect(mapStateToProps, null)(SearchPatients)

type PatientAction = {
    action: 'appointment' | 'pre-hospitalize' | 'hospitalize' | 'surgery' | string; // string for future actions
    patient: IPatient;
  }

interface SearchPatientsProps extends LocalizeContextProps {
    showResults: boolean;
    patients: IPatient[];
    investigation: any;
    personalFields: any[];
    permissions: any[];
    functionalities: any[];
    insurances: any[];
    coverages: any[];
    outpatientsInfo: any;
    initActionPatient: PatientAction | null;
    includeDeceased: boolean;
    setIncludeDeceased: (value: boolean) => void;
    searchPatientCallBack: (values: any) => void;
    searchAppointmentTodayCallBack: () => void;
    patientsTodayLoading: boolean;
    backToSearchCallBack: () => void;
    patientSelectedCallBack: (uuid: string) => void;  
    diagnoseSelectedCallBack: (ict: any) => void;
    billingPatientCallBack: (uuid: string) => void;
}


export const SearchPatientsComponent = withLocalize((props: SearchPatientsProps) => {
    const [actionPatient, setActionPatient] = useState<PatientAction | null>(props.initActionPatient);


    useEffect(() => {
        setActionPatient(props.initActionPatient);
    }, [props.initActionPatient]);

    function backToSearch(){
        props.backToSearchCallBack()
    }

    async function hospitalizePatient(uuid: string) {
        const fetchedPatient = await fetchPatient(uuid);
        if (fetchedPatient) {
            setActionPatient({patient: fetchedPatient, action : "pre-hospitalize"});
        }
    }
    async function billingPatient(uuid: string) {
        const fetchedPatient = await fetchPatient(uuid);
        if (fetchedPatient) {
            props.billingPatientCallBack(fetchedPatient.uuid);
        }
    }

    async function selectAppointmentPatient(uuid: string) {
        const fetchedPatient = await fetchPatient(uuid);
        if (fetchedPatient) {
            setActionPatient({action: "appointment", patient: fetchedPatient});
        }
    }

    async function surgeryPatient(uuid: string) {
        const fetchedPatient = await fetchPatient(uuid);
        if (fetchedPatient) {
            setActionPatient({action: "surgery", patient: fetchedPatient});
        }
    }
    
    function confirm(){
        setActionPatient({...actionPatient, action: "hospitalize"});
    }
    function resetModal(){
        setActionPatient(null);
    }

    function renderModal(){
        if(actionPatient?.patient){
            return (
                <Modal key="modal" open={Boolean(actionPatient.patient)} 
                    title={<Translate id={`hospital.search_patient.modal.${actionPatient.action}.title`}/>} 
                    closeModal={resetModal} >
                            <>
                            {
                            (actionPatient && actionPatient.action === 'pre-hospitalize')&&
                            <Grid container>
                                <Grid item xs={12}>
                                    <Typography variant="body1">
                                        <Translate id="hospital.search_patient.modal.pre-hospitalize.body" />

                                    </Typography>
                                    <PatientInfo uuidPatient={actionPatient.patient.uuid} />
                                </Grid>
                                <Grid item xs={12} style={{paddingTop:'1rem'}}>
                                    <ButtonCancel onClick={resetModal} data-testid="cancel-modal" color="primary" spaceright={1}>
                                        <Translate id="general.cancel" />
                                    </ButtonCancel>
                                    &nbsp;
                                    <ButtonContinue onClick={confirm} data-testid="continue-modal" color="green">
                                        <Translate id="general.continue" />
                                    </ButtonContinue>
                                </Grid>
                                
                            </Grid>
                        }
                        {
                            actionPatient && actionPatient.action === "hospitalize" &&
                            <DepartmentsAccordionRedux currentInvestigation={props.investigation} 
                                
                                uuidPatient={actionPatient.patient.uuid} />
                        }
                        {
                            actionPatient && actionPatient.action === "appointment" &&
                            <PatientAppointmentInfo uuidInvestigation={props.investigation.uuid}  uuidPatient={actionPatient.patient.uuid} 
                                outpatientsInfo={props.outpatientsInfo} phoneNumber={ Array(props.outpatientsInfo.sms) ? actionPatient.patient.personalData.phone : ""}
                                resetModal={resetModal}
                                appointmentMadeCallback={resetModal} /> 
                        }
                        {
                            actionPatient && actionPatient.action === "surgery" &&
                            <RequestForm uuidInvestigation={props.investigation.uuid}  uuidPatient={actionPatient.patient.uuid} 
                                serviceType={ServiceType.SURGERY} cancel={resetModal}
                                units={[]}
                                callBackRequestFinished={resetModal}/> 
                        }
                        </>
                </Modal>
            )   
        }
        else{
            return null;
        }
        
    }

    function renderCore(){
        if(!props.showResults){
            let personalFieldsForm = {}
            props.personalFields
                .filter((p: any) => p.type !== 'hidden')
                .sort((a,b) => a.order - b.order).forEach(personalField => {
                            const copyField = {...personalField};
                            copyField.required = false;
                            personalFieldsForm[personalField.name] = copyField
                        });
            if(Object.keys(personalFieldsForm).length === 0){
                personalFieldsForm["id"] = ID_FIELD;
            }      
            
            const hasDeceasedWorkflow = props.functionalities?.includes("DECEASED_WORKFLOW");
            const hasOutpatients = props.functionalities?.includes(FUNCTIONALITY.OUTPATIENTS);
            const tabLabels: any[] = [
                <Translate id="pages.hospital.search-patient.by-personal-details" />,
                <Translate id="pages.hospital.search-patient.by-diagnose" />,
            ];
            const tabPanels: any[] = [
                <Box label={<Translate id="pages.hospital.search-patient.by-personal-details" />}>
                    <Paper style={{padding:'1rem', margin:'1rem'}}>
                        <Translate id="pages.hospital.search-patient.note" />
                        <Form fields={personalFieldsForm}
                            submitText="investigation.search-patients.search" callBackForm={props.searchPatientCallBack} />
                        {hasDeceasedWorkflow && (
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={props.includeDeceased}
                                        onChange={(e) => props.setIncludeDeceased(e.target.checked)}
                                        size="small"
                                        data-testid="include-deceased-toggle"
                                    />
                                }
                                label={<Translate id="patient.status.include-deceased" />}
                                style={{ marginTop: '0.5rem' }}
                            />
                        )}
                    </Paper>
                </Box>,
                <Box label={<Translate id="pages.hospital.search-patient.by-diagnose" />}>
                    <Paper style={{padding:'1rem', margin:'1rem'}}>
                        <ICT language={props.activeLanguage.code} elementSelected={props.diagnoseSelectedCallBack}/>
                    </Paper>
                </Box>,
            ];
            // SHER-353: third modality, only for clinics with outpatients/agenda. No form —
            // one click lists every patient with an appointment today.
            if (hasOutpatients) {
                tabLabels.push(<Translate id="pages.hospital.search-patient.by-appointment-today" />);
                tabPanels.push(
                    <Box label={<Translate id="pages.hospital.search-patient.by-appointment-today" />}>
                        <Paper style={{padding:'1rem', margin:'1rem'}}>
                            <Typography variant="body1" gutterBottom>
                                <Translate id="pages.hospital.search-patient.appointment-today-note" />
                            </Typography>
                            {props.patientsTodayLoading
                                ? (
                                    <Box display="flex" alignItems="center" mt={1}>
                                        <CircularProgress size={20} />
                                        <Typography variant="body2" style={{marginLeft:'0.5rem'}}>
                                            <Translate id="pages.hospital.search-patient.appointment-today-loading" />
                                        </Typography>
                                    </Box>
                                )
                                : (
                                    <Button variant="contained" color="primary" data-testid="appointment-today-button"
                                        onClick={() => props.searchAppointmentTodayCallBack()}>
                                        <Translate id="pages.hospital.search-patient.appointment-today-button" />
                                    </Button>
                                )
                            }
                        </Paper>
                    </Box>
                );
            }
            // Auto-load the day's agenda the moment the "Cita hoy" tab is opened (no extra click).
            // It's always the last tab; guard against re-firing while a load is already in flight.
            const appointmentTodayTabIndex = hasOutpatients ? tabLabels.length - 1 : -1;
            return (
                <TabsSherwood initTab={0} name="Search Patients" labels={tabLabels}
                    tabChangeCallback={(idx) => {
                        if (idx === appointmentTodayTabIndex && !props.patientsTodayLoading) {
                            props.searchAppointmentTodayCallBack();
                        }
                    }}>
                    {tabPanels}
                </TabsSherwood>
            );
        }
        else{
            if(props.patients.length === 0){
                return ([
                    <Box mb={2}>
                        <Paper style={{padding:'1rem'}}>
                            No patients meet the criteria
                        </Paper>
                    </Box>,    
                    <ButtonBack mt={2} onClick={backToSearch}><Translate id="pages.hospital.search-patient.back-button" /></ButtonBack>,
                ])
            }
            else{
                let formSearch = [...props.personalFields].filter((p: any) => p.type !== 'hidden')
                if(formSearch.length === 0){
                    formSearch.push(ID_FIELD);
                } 
                console.log("Patients Results", props.patients);
                return(
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <PatientsTable patients={props.patients} 
                                personalFields={formSearch} permissions={props.permissions}
                                functionalities={props.functionalities}
                                insurances={props.insurances ? props.insurances : []}
                                coverages={props.coverages ? props.coverages : []}
                                showPatientCallBack={id => props.patientSelectedCallBack(id)} 
                                selectAppointmentPatientCallBack={(index) => selectAppointmentPatient(index)}
                                billingPatientCallBack={(index) => billingPatient(index)}
                                hospitalizePatientCallBack={(index) => hospitalizePatient(index)} 
                                surgeryPatientCallBack={(index) => surgeryPatient(index)}
                                />
                        </Grid>
                        <Grid item xs={12}>
                            <ButtonBack onClick={backToSearch}><Translate id="pages.hospital.search-patient.back-button" /></ButtonBack>
                        </Grid>
                    </Grid>)
            }
        }
    }
   
    
    return (
        <React.Fragment>
            {
                renderModal()
            }
            <Grid container spacing={2} >
                <GridContainer item xs={12}>
                    <SearchPatientIcon style={{fontSize:"2.5rem"}} />
                    <Typography variant="h1" gutterBottom display="inline" style={{marginBottom:"0px"}}>
                        <Translate id="pages.hospital.search-patient.title" />
                    </Typography>
                </GridContainer>
                
                <Grid item xs={12}>
                    {
                        renderCore()
                    }     
                </Grid>         
            </Grid>
        </React.Fragment>
    )
})